<script setup lang="ts">
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue';
import type { RouteLocationRaw } from '#vue-router';
import type { NavigationItem } from '~/types';
import { getNavItemUrl } from '~/utils/navigation';

const route = useRoute();

defineProps<{
  item: NavigationItem;
}>();

const popover = ref<(() => void) | null>(null);

// If route changes close the menu
watch(
  () => route.path,
  () => {
    return popover.value?.();
  },
);
</script>
<template>
  <NuxtLink
    v-if="!item.has_children"
    :href="getNavItemUrl(item) as RouteLocationRaw"
    class="text-totem-pole-800 dark:text-white hover:bg-totem-pole-500 hover:text-white transition duration-150 font-medium py-2 px-3 inline-flex items-center font-display outline-none rounded-button"
    exact-active-class="text-white bg-totem-pole-500"
    :target="item.open_in_new_tab ? '_blank' : '_self'"
  >
    {{ item.title }}
  </NuxtLink>

  <Popover v-else v-slot="{ close, open }" class="relative" as="div">
    <PopoverButton
      :ref="
        () => {
          popover = close;
        }
      "
      class="group text-totem-pole-800 dark:text-white hover:bg-totem-pole-500 hover:text-white transition duration-150 font-medium py-2 px-3 inline-flex items-center font-display outline-none rounded-button"
    >
      {{ item.title }}
      <Icon name="heroicons:chevron-down" class="flex-none w-5 ml-1 text-black dark:text-gray-400 group-hover:text-white dark:group-hover:text-white transition-transform duration-150" :class="{ 'rotate-180': open }" aria-hidden="true" />
    </PopoverButton>

    <transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="translate-y-1 opacity-0"
      enter-to-class="translate-y-0 opacity-100"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="translate-y-0 opacity-100"
      leave-to-class="translate-y-1 opacity-0"
    >
      <PopoverPanel
        class="absolute z-10 w-screen max-w-sm mt-2 overflow-hidden bg-white dark:bg-midnight-blue-800 shadow-lg rounded-panel top-full border border-neutral-200 dark:border-neutral-700"
      >
        <div class="p-2">
          <NuxtLink
            v-for="childItem in item.children as NavigationItem[]"
            :key="childItem.id"
            :href="getNavItemUrl(childItem) as RouteLocationRaw"
            :target="childItem.open_in_new_tab ? '_blank' : '_self'"
            class="relative flex items-center p-3 leading-6 transition duration-150 rounded-panel group gap-x-3 hover:bg-neutral-100 dark:hover:bg-neutral-700 text-neutral-800 dark:text-white"
          >
            <div
              class="flex items-center justify-center flex-none p-2 border rounded-button h-9 w-9 border-primary"
            >
              <Icon
                v-if="childItem.icon"
                :name="convertIconName(childItem.icon) as string"
                class="w-5 h-5 text-black dark:text-white"
              />
            </div>
            <div class="self-center">
              <p class="block font-medium text-neutral-900 dark:text-white font-display">
                {{ childItem.title }}
              </p>
              <p v-if="childItem.label" class="mt-0.5 text-sm leading-tight text-black dark:text-white">
                {{ childItem.label }}
              </p>
            </div>
          </NuxtLink>
        </div>
      </PopoverPanel>
    </transition>
  </Popover>
</template>
